import React from "react";
import {
    Box, 
    Typography, 
    Chip, 
    Stack, 
    Grid,
    Divider,
    useTheme,
    useMediaQuery,
    Card,
    CardContent
} from "@mui/material";
import { 
    Receipt, 
    Payment, 
    CalendarToday, 
} from "@mui/icons-material";

const money = (n) =>
    (n ?? 0).toLocaleString(undefined, { style: "currency", currency: "LKR", maximumFractionDigits: 0 });

export default function PaymentsList({ payments }) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isTablet = useMediaQuery(theme.breakpoints.down('md'));

    if (!payments || payments.length === 0) {
        return (
            <Box 
                sx={{ 
                    textAlign: 'center', 
                    py: 4,
                    px: 2,
                    bgcolor: 'grey.50',
                    borderRadius: 2,
                    border: '1px dashed',
                    borderColor: 'grey.300'
                }}
            >
                <Payment sx={{ fontSize: 48, color: 'grey.400', mb: 2 }} />
                <Typography variant="h6" color="text.secondary" mb={1}>
                    No Payments Yet
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    Payment history will appear here once payments are made.
                </Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ width: '100%' }}>
            <Stack spacing={2}>
                {payments.map((p, idx) => {
                    return (
                        <Card 
                            key={idx}
                            variant="outlined"
                            sx={{
                                transition: 'all 0.2s ease-in-out',
                                '&:hover': {
                                    boxShadow: theme.shadows[4],
                                    transform: 'translateY(-1px)',
                                },
                                borderRadius: 2,
                                bgcolor: 'background.paper'
                            }}
                        >
                            <CardContent sx={{ p: { xs: 1, sm: 3 }, '&:last-child': { pb: { xs: 2, sm: 3 } } }}>
                                {isMobile ? (
                                    // Mobile Layout - Vertical Stack
                                    <Stack spacing={2}>
                                        {/* Status Section */}
                                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                                            <Chip 
                                                icon={<Payment sx={{ fontSize: 16 }} />}
                                                label={p.status} 
                                                color={'default'}
                                                size="small"
                                                sx={{ 
                                                    fontWeight: 'bold',
                                                    '& .MuiChip-icon': {
                                                        fontSize: 16
                                                    }
                                                }}
                                            />
                                            <Typography 
                                                variant="h6" 
                                                fontWeight="bold" 
                                                color="info.main"
                                            >
                                                {money(p.payment)}
                                            </Typography>
                                        </Stack>

                                        <Divider />

                                        {/* Details Section */}
                                        <Stack spacing={1.5}>
                                            <Stack direction="row" alignItems="center" spacing={1}>
                                                <Receipt sx={{ fontSize: 18, color: 'text.secondary' }} />
                                                <Typography variant="body2" color="text.secondary">
                                                    Bill Number:
                                                </Typography>
                                                <Typography variant="body2" fontWeight="medium">
                                                    {p.bill_no}
                                                </Typography>
                                            </Stack>

                                            <Stack direction="row" alignItems="center" spacing={1}>
                                                <CalendarToday sx={{ fontSize: 18, color: 'text.secondary' }} />
                                                <Typography variant="body2" color="text.secondary">
                                                    Payment Date:
                                                </Typography>
                                                <Typography variant="body2" fontWeight="medium">
                                                    {p.paid_at}
                                                </Typography>
                                            </Stack>
                                        </Stack>
                                    </Stack>
                                ) : (
                                    // Desktop/Tablet Layout - Horizontal Grid
                                    <Grid container spacing={2} alignItems="center">
                                        {/* Status */}
                                        <Grid item xs={12} sm={3} md={2}>
                                            <Chip 
                                                icon={<Payment sx={{ fontSize: 16 }} />}
                                                label={p.status} 
                                                color={'default'}
                                                size="small"
                                                sx={{ 
                                                    fontWeight: 'bold',
                                                    width: { xs: 'auto', sm: '100%' },
                                                    justifyContent: 'center',
                                                    '& .MuiChip-icon': {
                                                        fontSize: 16
                                                    }
                                                }}
                                            />
                                        </Grid>

                                        {/* Bill Number */}
                                        <Grid item xs={12} sm={3} md={3}>
                                            <Stack direction="row" alignItems="center" spacing={1}>
                                                <Receipt sx={{ fontSize: 18, color: 'text.secondary' }} />
                                                <Box>
                                                    <Typography variant="caption" color="text.secondary">
                                                        Bill Number
                                                    </Typography>
                                                    <Typography variant="body2" fontWeight="medium">
                                                        {p.bill_no}
                                                    </Typography>
                                                </Box>
                                            </Stack>
                                        </Grid>

                                        {/* Payment Amount */}
                                        <Grid item xs={12} sm={3} md={3}>
                                            <Stack direction="row" alignItems="center" spacing={1}>
                                                <Payment sx={{ fontSize: 18, color: 'info.main' }} />
                                                <Box>
                                                    <Typography variant="caption" color="text.secondary">
                                                        Amount Paid
                                                    </Typography>
                                                    <Typography 
                                                        variant="h6" 
                                                        fontWeight="bold" 
                                                        color="info.main"
                                                    >
                                                        {money(p.payment)}
                                                    </Typography>
                                                </Box>
                                            </Stack>
                                        </Grid>

                                        {/* Payment Date */}
                                        <Grid item xs={12} sm={3} md={4}>
                                            <Stack direction="row" alignItems="center" spacing={1}>
                                                <CalendarToday sx={{ fontSize: 18, color: 'text.secondary' }} />
                                                <Box>
                                                    <Typography variant="caption" color="text.secondary">
                                                        Payment Date
                                                    </Typography>
                                                    <Typography variant="body2" fontWeight="medium">
                                                        {p.paid_at}
                                                    </Typography>
                                                </Box>
                                            </Stack>
                                        </Grid>
                                    </Grid>
                                )}
                            </CardContent>
                        </Card>
                    );
                })}
            </Stack>

            {/* Summary Footer */}
            {payments.length > 1 && (
                <Box sx={{ mt: 3, p: 2, bgcolor: 'primary.50', borderRadius: 2 }}>
                    <Stack 
                        direction={{ xs: 'column', sm: 'row' }} 
                        justifyContent="space-between" 
                        alignItems={{ xs: 'flex-start', sm: 'center' }}
                        spacing={1}
                    >
                        <Typography variant="body2" color="info.main" fontWeight="medium">
                            Total Payments: {payments.length}
                        </Typography>
                        <Typography variant="h6" color="primary.main" fontWeight="bold">
                            Total: {money(payments.reduce((sum, p) => sum + (p.payment || 0), 0))}
                        </Typography>
                    </Stack>
                </Box>
            )}
        </Box>
    );
}